import { useMemo, type ReactNode } from "react"
import { FunnelSimple, Info } from "@phosphor-icons/react"

import { Checkbox, IconButton } from "~/components/design-system/components"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import FilterPopover from "~/components/planning/FilterPopover"
import { useFilters } from "~/hooks/useFilters"
import {
  FilterFieldsOverrideContext,
  type FilterFieldsOverride,
} from "~/hooks/useFilterFieldsOverride"
import { usePermission } from "~/hooks/usePermission"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import type { FilterFieldType } from "~/utils/filter"
import { cn } from "~/utils/helpers"
import { filterRowsById } from "./AllocationFilters"
import { matchesStoreSearch, STORE_SEARCH_OPTIONS_NAME_AND_ADDRESS } from "~/utils/storeSearch"
import { useStoreSearch } from "./useStoreSearch"
import { distinctValues } from "~/utils/tsUtils"

export type StoreAssignmentRow = {
  id: string
  storeName: string
  city: string
  checked: boolean
  changed?: boolean
}

export type StoreAssignmentColumn<T> = {
  field: keyof T & string
  name: string
  type: "string" | "number"
  className: string
  render?: (row: T) => ReactNode
}

type StoreAssignmentTableProps<T extends StoreAssignmentRow> = {
  scopeKey: string
  note: ReactNode
  rows: T[]
  columns: StoreAssignmentColumn<T>[]
  targetLabel: string
  onAssign: (storeIds: string[], checked: boolean) => void
  /** Locks the table and explains why, e.g. when the target is not active. */
  disabledReason?: string
  loading?: boolean
}

export function StoreAssignmentTable<T extends StoreAssignmentRow>({
  scopeKey,
  ...props
}: StoreAssignmentTableProps<T>) {
  return (
    <FilterScopeProvider scopeKey={scopeKey}>
      <Table scopeKey={scopeKey} {...props} />
    </FilterScopeProvider>
  )
}

function emptyText(loading: boolean, rowCount: number) {
  if (loading) return "Loading stores…"
  if (rowCount === 0) return "No stores yet. Stores load with the retail location data."
  return "No stores match the search or filters."
}

function cellText(value: unknown) {
  if (Array.isArray(value)) return value.join(", ")
  return value == null ? "" : String(value)
}

function Table<T extends StoreAssignmentRow>({
  note,
  rows,
  columns,
  targetLabel,
  onAssign,
  disabledReason,
  loading = false,
  scopeKey,
}: StoreAssignmentTableProps<T>) {
  const disabled = disabledReason !== undefined
  const { search, setSearch, searchBy, setSearchBy } = useStoreSearch(
    `store-assignment-${scopeKey}`,
    STORE_SEARCH_OPTIONS_NAME_AND_ADDRESS,
  )
  const { filters } = useFilters()
  const { allowed, reason } = usePermission("edit")
  const locked = disabled || !allowed

  const filterFields = useMemo<FilterFieldType[]>(
    () => [
      { value: "storeName", name: "Store", type: "string" },
      ...columns.map((column) => ({ value: column.field, name: column.name, type: column.type })),
    ],
    [columns],
  )
  const fieldsOverride = useMemo<FilterFieldsOverride>(() => {
    const optionsByField: Record<string, string[]> = {
      storeName: distinctValues(rows.map((row) => row.storeName)),
    }
    for (const column of columns) {
      if (column.type !== "string") continue
      optionsByField[column.field] = distinctValues(
        rows.flatMap((row) => {
          const value = row[column.field]
          return Array.isArray(value) ? value : [cellText(value)]
        }),
      )
    }
    return { fields: filterFields, optionsByField }
  }, [rows, columns, filterFields])

  const visible = useMemo(() => {
    const searched = rows.filter((row) =>
      matchesStoreSearch({ name: row.storeName, address: row.city }, search, searchBy),
    )
    const fieldType = (field: string) =>
      filterFields.find((candidate) => candidate.value === field)?.type
    return filterRowsById(searched, filters, fieldType)
  }, [rows, filters, search, searchBy, filterFields])

  const checkedCount = visible.filter((row) => row.checked).length
  const allChecked = visible.length > 0 && checkedCount === visible.length
  const toggle = (row: T) => {
    if (!locked) onAssign([row.id], !row.checked)
  }
  const toggleAll = () =>
    onAssign(
      visible.map((row) => row.id),
      !allChecked,
    )

  return (
    <FilterFieldsOverrideContext.Provider value={fieldsOverride}>
      <div className="mb-2 flex items-center gap-2">
        <div className="paragraph-xs flex min-w-0 flex-1 items-center gap-1.5 text-fg-muted">
          <Info className="size-3.5 shrink-0" />
          <span className="truncate">{note}</span>
        </div>
        <ExpandableSearchField
          initialSearchText={search}
          onSearchTextChange={setSearch}
          placeholder="Search stores..."
          debounceTime={200}
          searchBy={searchBy}
          onSearchByChange={setSearchBy}
          searchByOptions={STORE_SEARCH_OPTIONS_NAME_AND_ADDRESS}
        />
        <FilterPopover
          align="end"
          editGroup
          customTrigger={
            <IconButton variant="neutral" size="sm" aria-label="Add filter">
              <FunnelSimple />
            </IconButton>
          }
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-base">
        <div className="label-sm flex items-center gap-2.5 border-b border-base bg-subtle px-3 py-1.5 text-fg-muted">
          <Checkbox
            checked={allChecked}
            indeterminate={checkedCount > 0 && !allChecked}
            onCheckedChange={toggleAll}
            disabled={disabled || visible.length === 0}
            requires="edit"
            allowedTitle={disabledReason}
            aria-label={`Assign all shown stores to ${targetLabel}`}
            className="size-3.5 rounded-xs"
            iconClassName="size-3"
          />
          <span className="flex-1">Store</span>
          {columns.map((column) => (
            <span key={column.field} className={cn("shrink-0", column.className)}>
              {column.name}
            </span>
          ))}
        </div>
        {visible.map((row) => (
          <div
            key={row.id}
            title={disabledReason ?? reason}
            className={cn(
              "flex items-center gap-2.5 border-b border-base px-3 py-1.5 last:border-b-0",
              !locked && "cursor-pointer hover:bg-subtle",
              row.changed && "bg-adjusted-bg",
            )}
            onClick={() => toggle(row)}
          >
            <Checkbox
              checked={row.checked}
              onCheckedChange={() => toggle(row)}
              onClick={(event) => event.stopPropagation()}
              disabled={disabled}
              requires="edit"
              allowedTitle={disabledReason}
              aria-label={`Assign ${row.storeName} to ${targetLabel}`}
              className="size-3.5 rounded-xs"
              iconClassName="size-3"
            />
            <span className="paragraph-xs flex-1 truncate font-medium">{row.storeName}</span>
            {columns.map((column) => (
              <span
                key={column.field}
                className={cn("paragraph-xs shrink-0 truncate text-fg-subtle", column.className)}
              >
                {column.render ? column.render(row) : cellText(row[column.field])}
              </span>
            ))}
          </div>
        ))}
        {visible.length === 0 && (
          <div className="paragraph-xs px-3 py-4 text-fg-muted">
            {emptyText(loading, rows.length)}
          </div>
        )}
      </div>
    </FilterFieldsOverrideContext.Provider>
  )
}
